import { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  ConfirmDialog,
  EmptyState,
  Input,
  Modal,
  Select,
  Textarea,
  useToast,
} from '@adop/ui';
import {
  ArrowDown,
  ArrowUp,
  Copy,
  Pencil,
  Plus,
  Tags,
  Trash2,
} from 'lucide-react';
import { centsToDollarString, dollarsToCents } from '@adop/shared';
import type { MenuItem } from '@adop/shared';
import { useWorkspace } from '../../lib/workspace-context';
import { useAsync } from '../../lib/useAsync';
import {
  copyMenu,
  deleteMenuCategory,
  deleteMenuItem,
  deleteModifierGroup,
  deleteModifierOption,
  fetchMenu,
  insertMenuCategory,
  insertMenuItem,
  insertModifierGroup,
  insertModifierOption,
  replaceItemModifierGroups,
  updateMenuCategory,
  updateMenuItem,
  updateModifierGroup,
  updateModifierOption,
} from '../../lib/data';
import { moveItem, resequence } from '../../lib/sort';
import type { MenuSnapshot } from '../../lib/menu';
import { errorMessage } from '../../lib/api';
import { PageHeader } from '../../components/PageHeader';
import { ImageUpload } from '../../components/ImageUpload';
import { ListSkeleton } from '../../components/LoadingState';
import { Alert } from '../../components/Alert';

interface Scope {
  restaurantId: string;
  locationId: string;
}

interface ItemForm {
  id: string | null;
  categoryId: string;
  name: string;
  description: string;
  price: string;
  imageKey: string | null;
  available: boolean;
  tags: string;
}

function emptyItemForm(categoryId: string): ItemForm {
  return {
    id: null,
    categoryId,
    name: '',
    description: '',
    price: '',
    imageKey: null,
    available: true,
    tags: '',
  };
}

function itemToForm(item: MenuItem): ItemForm {
  return {
    id: item.id,
    categoryId: item.category_id,
    name: item.name,
    description: item.description ?? '',
    price: centsToDollarString(item.price_cents),
    imageKey: item.image_key,
    available: item.available,
    tags: item.tags.join(', '),
  };
}

interface GroupForm {
  id: string | null;
  name: string;
  min: number;
  max: number;
  required: boolean;
}

export function MenuPage() {
  const { location, canManage, visibleLocations } = useWorkspace();
  const { toast } = useToast();
  const state = useAsync<MenuSnapshot>(
    () => (location ? fetchMenu(location.id) : Promise.resolve({ categories: [], items: [], groups: [], options: [], links: [] })),
    [location?.id],
  );
  const [tab, setTab] = useState<'menu' | 'modifiers'>('menu');
  const [itemForm, setItemForm] = useState<ItemForm | null>(null);
  const [groupForm, setGroupForm] = useState<GroupForm | null>(null);
  const [optionForm, setOptionForm] = useState<{ groupId: string; id: string | null; name: string; price: string } | null>(null);
  const [categoryModal, setCategoryModal] = useState<{ id: string | null; name: string } | null>(null);
  const [groupAssignment, setGroupAssignment] = useState<{ item: MenuItem; groupIds: string[] } | null>(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyFrom, setCopyFrom] = useState('');
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ kind: string; id: string; name: string } | null>(null);

  const scope: Scope | null = location
    ? { restaurantId: location.restaurant_id, locationId: location.id }
    : null;

  const menu = state.data;
  const categories = useMemo(
    () => (menu ? [...menu.categories].sort((a, b) => a.sort_order - b.sort_order) : []),
    [menu],
  );

  const run = async (action: () => Promise<unknown>, success: string) => {
    setBusy(true);
    try {
      await action();
      toast({ title: success, variant: 'success' });
      state.reload();
    } catch (cause) {
      toast({ title: 'Something went wrong', description: errorMessage(cause), variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const toggleItemAvailability = async (item: MenuItem) => {
    await run(() => updateMenuItem(item.id, { available: !item.available }), item.available ? 'Item 86’d' : 'Item available');
  };

  const saveItem = async () => {
    if (!itemForm || !scope) return;
    const cents = dollarsToCents(itemForm.price);
    if (cents === null) {
      toast({ title: 'Enter a valid price', variant: 'error' });
      return;
    }
    const tags = itemForm.tags
      .split(',')
      .map((tag) => tag.trim())
      .filter((tag) => tag.length > 0);
    const payload = {
      category_id: itemForm.categoryId,
      name: itemForm.name.trim(),
      description: itemForm.description.trim() || null,
      price_cents: cents,
      image_key: itemForm.imageKey,
      available: itemForm.available,
      tags,
    };
    if (!payload.name) {
      toast({ title: 'Item name is required', variant: 'error' });
      return;
    }
    if (itemForm.id) {
      await run(() => updateMenuItem(itemForm.id as string, payload), 'Item updated');
    } else {
      await run(
        () =>
          insertMenuItem({
            restaurant_id: scope.restaurantId,
            location_id: scope.locationId,
            sort_order: menu ? menu.items.filter((item) => item.category_id === itemForm.categoryId).length : 0,
            ...payload,
          }),
        'Item added',
      );
    }
    setItemForm(null);
  };

  const reorderCategories = async (index: number, direction: 'up' | 'down') => {
    const next = moveItem(categories, index, direction);
    await run(
      () => Promise.all(resequence(next).map((row) => updateMenuCategory(row.id, { sort_order: row.sort_order }))),
      'Order saved',
    );
  };

  const reorderItems = async (items: MenuItem[], index: number, direction: 'up' | 'down') => {
    const next = moveItem(items, index, direction);
    await run(
      () => Promise.all(resequence(next).map((row) => updateMenuItem(row.id, { sort_order: row.sort_order }))),
      'Order saved',
    );
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const { kind, id } = deleteTarget;
    const action =
      kind === 'category'
        ? () => deleteMenuCategory(id)
        : kind === 'item'
          ? () => deleteMenuItem(id)
          : kind === 'group'
            ? () => deleteModifierGroup(id)
            : () => deleteModifierOption(id);
    await run(action, 'Deleted');
    setDeleteTarget(null);
  };

  const saveGroup = async () => {
    if (!groupForm || !scope) return;
    const payload = {
      name: groupForm.name.trim(),
      min_select: groupForm.min,
      max_select: groupForm.max,
      required: groupForm.required,
    };
    if (!payload.name) {
      toast({ title: 'Group name is required', variant: 'error' });
      return;
    }
    if (groupForm.id) {
      await run(() => updateModifierGroup(groupForm.id as string, payload), 'Group updated');
    } else {
      await run(
        () =>
          insertModifierGroup({
            restaurant_id: scope.restaurantId,
            location_id: scope.locationId,
            ...payload,
          }),
        'Group added',
      );
    }
    setGroupForm(null);
  };

  const saveOption = async () => {
    if (!optionForm || !scope) return;
    const cents = dollarsToCents(optionForm.price);
    if (cents === null) {
      toast({ title: 'Enter a valid price', variant: 'error' });
      return;
    }
    const payload = { name: optionForm.name.trim(), price_delta_cents: cents };
    if (!payload.name) {
      toast({ title: 'Option name is required', variant: 'error' });
      return;
    }
    if (optionForm.id) {
      await run(() => updateModifierOption(optionForm.id as string, payload), 'Option updated');
    } else {
      await run(
        () =>
          insertModifierOption({
            restaurant_id: scope.restaurantId,
            location_id: scope.locationId,
            group_id: optionForm.groupId,
            sort_order: menu ? menu.options.filter((option) => option.group_id === optionForm.groupId).length : 0,
            ...payload,
          }),
        'Option added',
      );
    }
    setOptionForm(null);
  };

  const saveGroupAssignment = async () => {
    if (!groupAssignment || !scope) return;
    await run(
      () => replaceItemModifierGroups(scope, groupAssignment.item.id, groupAssignment.groupIds),
      'Modifiers updated',
    );
    setGroupAssignment(null);
  };

  const runCopy = async () => {
    if (!location || !copyFrom) return;
    await run(() => copyMenu(copyFrom, location.id), 'Menu copied');
    setCopyOpen(false);
  };

  if (!location || !scope) {
    return <EmptyState icon={<Tags className="h-6 w-6" />} title="Select a location" />;
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Menu"
        description={canManage ? 'Build your categories, items and modifiers.' : 'Staff can mark items and options unavailable.'}
        action={
          <div className="flex gap-2">
            {canManage ? (
              <>
                <Button variant="outline" size="sm" onClick={() => setCopyOpen(true)}>
                  <Copy className="h-4 w-4" /> Copy menu
                </Button>
                <Button size="sm" onClick={() => setCategoryModal({ id: null, name: '' })}>
                  <Plus className="h-4 w-4" /> Category
                </Button>
              </>
            ) : null}
          </div>
        }
      />

      {state.error ? <Alert>{state.error}</Alert> : null}

      <div className="flex gap-1 rounded-lg border border-zinc-200 bg-white p-1">
        {(['menu', 'modifiers'] as const).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setTab(value)}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium ${
              tab === value ? 'bg-zinc-900 text-white' : 'text-zinc-600 hover:bg-zinc-100'
            }`}
          >
            {value === 'menu' ? 'Categories & items' : 'Modifier groups'}
          </button>
        ))}
      </div>

      {state.loading ? (
        <ListSkeleton />
      ) : tab === 'menu' ? (
        categories.length === 0 ? (
          <EmptyState
            icon={<Tags className="h-6 w-6" />}
            title="No menu yet"
            description="Add your first category to start building the menu."
          />
        ) : (
          <div className="space-y-4">
            {categories.map((category, categoryIndex) => {
              const items = (menu?.items ?? [])
                .filter((item) => item.category_id === category.id)
                .sort((a, b) => a.sort_order - b.sort_order);
              return (
                <Card key={category.id}>
                  <CardHeader>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <CardTitle>{category.name}</CardTitle>
                        <Badge variant={category.available ? 'success' : 'default'}>
                          {category.available ? 'Available' : 'Hidden'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1">
                        {canManage ? (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label="Move category up"
                              disabled={categoryIndex === 0 || busy}
                              onClick={() => void reorderCategories(categoryIndex, 'up')}
                            >
                              <ArrowUp className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              aria-label="Move category down"
                              disabled={categoryIndex === categories.length - 1 || busy}
                              onClick={() => void reorderCategories(categoryIndex, 'down')}
                            >
                              <ArrowDown className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setCategoryModal({ id: category.id, name: category.name })}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteTarget({ kind: 'category', id: category.id, name: category.name })}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </>
                        ) : null}
                        <Checkbox
                          label="Available"
                          checked={category.available}
                          onChange={() =>
                            void run(
                              () => updateMenuCategory(category.id, { available: !category.available }),
                              'Category updated',
                            )
                          }
                        />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {items.map((item, itemIndex) => (
                        <div
                          key={item.id}
                          className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-200 p-3"
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className="truncate text-sm font-medium text-zinc-900">{item.name}</p>
                              <Badge variant={item.available ? 'success' : 'danger'}>
                                {item.available ? 'Available' : '86’d'}
                              </Badge>
                            </div>
                            <p className="text-xs text-zinc-500">
                              {centsToDollarString(item.price_cents) && `$${centsToDollarString(item.price_cents)}`}
                              {item.tags.length > 0 ? ` · ${item.tags.join(', ')}` : ''}
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => void toggleItemAvailability(item)}
                            className="rounded-lg border border-zinc-200 px-3 py-1.5 text-xs font-medium hover:bg-zinc-50"
                          >
                            {item.available ? 'Mark 86’d' : 'Mark available'}
                          </button>
                          {canManage ? (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                aria-label="Move item up"
                                disabled={itemIndex === 0 || busy}
                                onClick={() => void reorderItems(items, itemIndex, 'up')}
                              >
                                <ArrowUp className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                aria-label="Move item down"
                                disabled={itemIndex === items.length - 1 || busy}
                                onClick={() => void reorderItems(items, itemIndex, 'down')}
                              >
                                <ArrowDown className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                  setGroupAssignment({
                                    item,
                                    groupIds: (menu?.links ?? [])
                                      .filter((link) => link.item_id === item.id)
                                      .sort((a, b) => a.sort_order - b.sort_order)
                                      .map((link) => link.group_id),
                                  })
                                }
                              >
                                Modifiers
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setItemForm(itemToForm(item))}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteTarget({ kind: 'item', id: item.id, name: item.name })}
                              >
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            </>
                          ) : null}
                        </div>
                      ))}
                      {canManage ? (
                        <Button variant="outline" size="sm" onClick={() => setItemForm(emptyItemForm(category.id))}>
                          <Plus className="h-4 w-4" /> Add item
                        </Button>
                      ) : null}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )
      ) : (
        <div className="space-y-4">
          {canManage ? (
            <Button size="sm" onClick={() => setGroupForm({ id: null, name: '', min: 0, max: 1, required: false })}>
              <Plus className="h-4 w-4" /> Modifier group
            </Button>
          ) : null}
          {(menu?.groups ?? []).length === 0 ? (
            <EmptyState
              icon={<Tags className="h-6 w-6" />}
              title="No modifier groups"
              description="Create groups like Size or Extras, then attach them to items."
            />
          ) : (
            (menu?.groups ?? []).map((group) => {
              const options = (menu?.options ?? []).filter((option) => option.group_id === group.id);
              return (
                <Card key={group.id}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>{group.name}</CardTitle>
                        <p className="text-xs text-zinc-500">
                          {group.required ? 'Required' : 'Optional'} · {group.min_select}–{group.max_select} choices
                        </p>
                      </div>
                      {canManage ? (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setGroupForm({
                                id: group.id,
                                name: group.name,
                                min: group.min_select,
                                max: group.max_select,
                                required: group.required,
                              })
                            }
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteTarget({ kind: 'group', id: group.id, name: group.name })}
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      ) : null}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {options.map((option) => (
                        <div key={option.id} className="flex items-center gap-3 rounded-lg border border-zinc-200 p-2.5">
                          <span className="flex-1 text-sm text-zinc-900">{option.name}</span>
                          <span className="text-sm text-zinc-500">
                            {option.price_delta_cents !== 0 ? `+$${centsToDollarString(option.price_delta_cents)}` : 'Included'}
                          </span>
                          <Checkbox
                            label="Available"
                            checked={option.available}
                            onChange={() =>
                              void run(
                                () => updateModifierOption(option.id, { available: !option.available }),
                                'Option updated',
                              )
                            }
                          />
                          {canManage ? (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  setOptionForm({
                                    groupId: group.id,
                                    id: option.id,
                                    name: option.name,
                                    price: centsToDollarString(option.price_delta_cents),
                                  })
                                }
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDeleteTarget({ kind: 'option', id: option.id, name: option.name })}
                              >
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            </>
                          ) : null}
                        </div>
                      ))}
                      {canManage ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setOptionForm({ groupId: group.id, id: null, name: '', price: '0' })}
                        >
                          <Plus className="h-4 w-4" /> Add option
                        </Button>
                      ) : null}
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      )}

      {/* Category modal */}
      <Modal
        open={categoryModal !== null}
        onClose={() => setCategoryModal(null)}
        title={categoryModal?.id ? 'Rename category' : 'New category'}
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setCategoryModal(null)}>
              Cancel
            </Button>
            <Button
              loading={busy}
              onClick={() => {
                if (!categoryModal) return;
                const name = categoryModal.name.trim();
                if (!name) return;
                run(
                  () =>
                    categoryModal.id
                      ? updateMenuCategory(categoryModal.id, { name })
                      : insertMenuCategory(scope, { name, sort_order: categories.length }),
                  'Category saved',
                ).then(() => setCategoryModal(null));
              }}
            >
              Save
            </Button>
          </>
        }
      >
        <Input
          label="Category name"
          value={categoryModal?.name ?? ''}
          onChange={(event) => setCategoryModal((current) => (current ? { ...current, name: event.target.value } : current))}
        />
      </Modal>

      {/* Item modal */}
      <Modal
        open={itemForm !== null}
        onClose={() => setItemForm(null)}
        title={itemForm?.id ? 'Edit item' : 'New item'}
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={() => setItemForm(null)}>
              Cancel
            </Button>
            <Button loading={busy} onClick={() => void saveItem()}>
              Save item
            </Button>
          </>
        }
      >
        {itemForm ? (
          <div className="space-y-4">
            <Input
              label="Name"
              value={itemForm.name}
              onChange={(event) => setItemForm({ ...itemForm, name: event.target.value })}
            />
            <Textarea
              label="Description"
              value={itemForm.description}
              onChange={(event) => setItemForm({ ...itemForm, description: event.target.value })}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Price (USD)"
                value={itemForm.price}
                inputMode="decimal"
                placeholder="12.50"
                onChange={(event) => setItemForm({ ...itemForm, price: event.target.value })}
              />
              <Input
                label="Tags"
                hint="Comma separated, e.g. vegetarian, spicy"
                value={itemForm.tags}
                onChange={(event) => setItemForm({ ...itemForm, tags: event.target.value })}
              />
            </div>
            <ImageUpload
              locationId={scope.locationId}
              value={itemForm.imageKey}
              onChange={(key) => setItemForm({ ...itemForm, imageKey: key })}
              label="Item photo"
            />
            <Checkbox
              label="Available"
              checked={itemForm.available}
              onChange={(event) => setItemForm({ ...itemForm, available: event.target.checked })}
            />
          </div>
        ) : null}
      </Modal>

      {/* Group modal */}
      <Modal
        open={groupForm !== null}
        onClose={() => setGroupForm(null)}
        title={groupForm?.id ? 'Edit modifier group' : 'New modifier group'}
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setGroupForm(null)}>
              Cancel
            </Button>
            <Button loading={busy} onClick={() => void saveGroup()}>
              Save group
            </Button>
          </>
        }
      >
        {groupForm ? (
          <div className="space-y-4">
            <Input
              label="Name"
              value={groupForm.name}
              onChange={(event) => setGroupForm({ ...groupForm, name: event.target.value })}
            />
            <div className="grid grid-cols-2 gap-4">
              <Input
                label="Min choices"
                type="number"
                min={0}
                value={groupForm.min}
                onChange={(event) => setGroupForm({ ...groupForm, min: Number(event.target.value) })}
              />
              <Input
                label="Max choices"
                type="number"
                min={1}
                value={groupForm.max}
                onChange={(event) => setGroupForm({ ...groupForm, max: Number(event.target.value) })}
              />
            </div>
            <Checkbox
              label="Required"
              checked={groupForm.required}
              onChange={(event) => setGroupForm({ ...groupForm, required: event.target.checked })}
            />
          </div>
        ) : null}
      </Modal>

      {/* Option modal */}
      <Modal
        open={optionForm !== null}
        onClose={() => setOptionForm(null)}
        title={optionForm?.id ? 'Edit option' : 'New option'}
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setOptionForm(null)}>
              Cancel
            </Button>
            <Button loading={busy} onClick={() => void saveOption()}>
              Save option
            </Button>
          </>
        }
      >
        {optionForm ? (
          <div className="space-y-4">
            <Input
              label="Name"
              value={optionForm.name}
              onChange={(event) => setOptionForm({ ...optionForm, name: event.target.value })}
            />
            <Input
              label="Price delta (USD)"
              value={optionForm.price}
              inputMode="decimal"
              onChange={(event) => setOptionForm({ ...optionForm, price: event.target.value })}
            />
          </div>
        ) : null}
      </Modal>

      {/* Item group assignment */}
      <Modal
        open={groupAssignment !== null}
        onClose={() => setGroupAssignment(null)}
        title={groupAssignment ? `Modifiers for ${groupAssignment.item.name}` : ''}
        size="md"
        footer={
          <>
            <Button variant="outline" onClick={() => setGroupAssignment(null)}>
              Cancel
            </Button>
            <Button loading={busy} onClick={() => void saveGroupAssignment()}>
              Save
            </Button>
          </>
        }
      >
        {groupAssignment ? (
          <div className="space-y-3">
            {(menu?.groups ?? []).length === 0 ? (
              <p className="text-sm text-zinc-500">Create modifier groups first.</p>
            ) : (
              (menu?.groups ?? []).map((group) => {
                const index = groupAssignment.groupIds.indexOf(group.id);
                const checked = index >= 0;
                return (
                  <div key={group.id} className="flex items-center gap-2 rounded-lg border border-zinc-200 p-2.5">
                    <Checkbox
                      label={group.name}
                      checked={checked}
                      onChange={(event) => {
                        const ids = event.target.checked
                          ? [...groupAssignment.groupIds, group.id]
                          : groupAssignment.groupIds.filter((id) => id !== group.id);
                        setGroupAssignment({ ...groupAssignment, groupIds: ids });
                      }}
                    />
                    {checked ? (
                      <div className="ml-auto flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label="Move up"
                          disabled={index === 0}
                          onClick={() =>
                            setGroupAssignment({
                              ...groupAssignment,
                              groupIds: moveItem(groupAssignment.groupIds, index, 'up'),
                            })
                          }
                        >
                          <ArrowUp className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          aria-label="Move down"
                          disabled={index === groupAssignment.groupIds.length - 1}
                          onClick={() =>
                            setGroupAssignment({
                              ...groupAssignment,
                              groupIds: moveItem(groupAssignment.groupIds, index, 'down'),
                            })
                          }
                        >
                          <ArrowDown className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : null}
                  </div>
                );
              })
            )}
          </div>
        ) : null}
      </Modal>

      {/* Copy menu */}
      <Modal
        open={copyOpen}
        onClose={() => setCopyOpen(false)}
        title="Copy menu from another location"
        size="sm"
        footer={
          <>
            <Button variant="outline" onClick={() => setCopyOpen(false)}>
              Cancel
            </Button>
            <Button loading={busy} disabled={!copyFrom} onClick={() => void runCopy()}>
              Copy menu
            </Button>
          </>
        }
      >
        <Select
          label="Copy from"
          value={copyFrom}
          onChange={(event) => setCopyFrom(event.target.value)}
        >
          <option value="">Choose a location…</option>
          {visibleLocations
            .filter((entry) => entry.id !== location.id)
            .map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
        </Select>
        <p className="mt-2 text-xs text-zinc-500">
          This appends a copy of the other location&apos;s categories, items and modifiers. Existing rows are
          left untouched.
        </p>
      </Modal>

      <ConfirmDialog
        open={deleteTarget !== null}
        title={`Delete ${deleteTarget?.name ?? ''}?`}
        message="This cannot be undone."
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => void confirmDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
